<template lang="pug">
  header.header(
    :class="{ 'show-menu': isMenuOpened, 'is-out': !isDone, 'is-scrolled': isScrolled }"
  )
    VOverlay(
      :value="isMenuOpened"
      @click="closeMenu"
    )
    VContainer
      a(href="/").header__logo
        IOLogo
      .header__nav
        //- Connect wallet button for mobile
        .header__wallet
          VBtn.v-btn--toggle(
            v-if="isUserConnected || (isMobile && !noSaleStarted)"
            x-large
            rounded
            :class="{ 'is-connected': isUserConnected }"
            :elevation="0"
            @click="toggleWalletCheck"
          ) {{ walletText }}
          transition(name="fade")
            span(v-if="isMobile && isUserConnected && showDisconnectTooltip") Tap Again to Disconnect
        VBtn.v-btn--menu(
          fab
          dark
          :elevation="0"
          @click="openMenu"
        )
          span.vh Click to open Menu
          VIcon mdi-menu
      transition(
        name="slide-left"
      )
        IOSidebar(
          v-if="isMenuOpened"
          @close="closeMenu"
        )
</template>

<script>
import accountMixin from '@/mixins/account-mixins';
import saleMixin from '@/mixins/sale-mixins';

export default {
  name: 'IOHeader',
  components: {
    IOLogo: () => import('@/components/IOLogo'),
    IOSidebar: () => import('@/components/IOSidebar'),
  },
  mixins: [accountMixin, saleMixin],
  props: {
    isDone: { type: Boolean, default: false },
    isMobile: { type: Boolean, default: false },
  },
  data() {
    return {
      isMenuOpened: false,
      isScrolled: false,
      showDisconnectTooltip: false,
    };
  },
  computed: {
    walletText() {
      return this.isUserConnected
        ? this.isMobile
          ? this.getActiveAccountRedacted
          : 'Disconnect Wallet'
        : 'Connect Wallet';
    },
  },
  methods: {
    checkScroll(y) {
      const position = y || window.scrollY;
      if (position > 100) {
        this.isScrolled = true;
      } else {
        this.isScrolled = false;
      }
    },
    openMenu() {
      this.isMenuOpened = true;
    },
    closeMenu() {
      this.isMenuOpened = false;
    },
    toggleWalletCheck() {
      if (!this.isMobile) {
        this.toggleWallet();
      } else {
        if (this.isUserConnected && !this.showDisconnectTooltip) {
          this.showDisconnectTooltip = true;

          setTimeout(() => {
            this.showDisconnectTooltip = false;
          }, 3000);
        } else {
          this.toggleWallet();
          this.showDisconnectTooltip = false;
        }
      }
    },
  },
};
</script>

<style lang="scss">
.header {
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  z-index: 7;
  padding: 10px 0;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.5) 10%, rgba(0, 0, 0, 0) 100%);
  transition: transform 1s 0.75s cubic-bezier(0, 0, 0, 1);

  &::before {
    content: '';
    display: block;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    position: absolute;
    background: $black;
    transition: transform 0.5s cubic-bezier(0, 0, 0, 1);
  }

  &:not(.is-scrolled) {
    &::before {
      transform: translateY(-100%);
    }
  }

  &.is-out {
    transform: translateY(-100%);
  }

  .container {
    max-width: 1800px;
    display: flex;
    justify-content: space-between;
    align-content: center;
    align-items: center;
    position: relative;
    z-index: 6;

    @include big {
      max-width: none;
      width: 90%;
    }
  }
}

.header__logo {
  display: block;
  width: 110px;

  @include mobile {
    width: 82px;
  }

  @include wide-desktop {
    width: 140px;
  }

  svg {
    width: 100%;
    height: auto;
    display: block;

    path,
    ellipse {
      fill: $white;
    }

    circle {
      stroke: $white;
    }
  }
}

.header__nav {
  margin-left: auto;
  width: 60%;
  display: flex;
  justify-content: flex-end;
  align-content: center;
  align-items: center;

  .header__wallet {
    position: relative;

    .v-btn.v-btn--toggle {
      font-family: $font-primary;
      font-weight: 800;
      min-width: 140px;
      height: 40px;
      font-size: 0.75rem;
      margin-right: 10px;
      padding: 0 10px;
      transition: background-color 0.5s cubic-bezier(0, 0, 0, 1),
        color 0.5s cubic-bezier(0, 0, 0, 1);

      &.is-connected {
        background: rgba(0, 0, 0, 0.5);
        color: #fff;
      }
    }

    > span {
      position: absolute;
      width: 176px;
      left: calc(calc(100% - 186px) / 2);
      bottom: -40px;

      background: rgba(97, 97, 97, 0.9);
      color: $white;
      border-radius: 4px;
      font-size: 0.875rem;
      padding: 5px 16px;
      text-transform: none;
      text-transform: initial;
      pointer-events: none;
    }
  }

  .v-btn--menu {
    transition: background-color 0.5s cubic-bezier(0, 0, 0, 1);
    height: 40px;
    width: 40px;

    @include wide-desktop {
      height: 56px;
      width: 56px;
    }

    .v-icon {
      transition: color 0.5s cubic-bezier(0, 0, 0, 1);
    }

    .header.is-scrolled & {
      background: $white;

      .v-icon {
        color: $black;
      }
    }
  }
}

ul.header__social {
  list-style: none;
  margin: 0;
  padding: 0 !important;
  display: inline-block;
  margin-right: 30px;

  .social__item {
    display: inline-block;
    margin: 0 10px 0 20px;
  }

  .social__link {
    text-decoration: none;
    transition: opacity 0.3s cubic-bezier(0, 0, 0, 1);

    &:hover {
      opacity: 0.6;
    }
  }

  .v-icon {
    color: $white;
    font-size: 2rem;
  }
}
</style>
